import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Flex, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Heading, Text, Link, Image } from '@chakra-ui/react';
import { FiHome, FiClock, FiEye } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import i18n from 'i18next';

import { Position } from '../../components/Widget/types';
import Widget, { ListShare } from '../../components/Widget';
import Sticky from '../../components/Sticky';
import Share from '../../components/Share';
import Card from '../../components/Card';
import Date from '../../components/Date';
import { getTitle, hexToRgba } from '../../utils';
import useStory from './useStory';
import FollowUs from './FollowUs';
import Related from './Related';

function Story(): React.ReactElement {
  const { pathname, borderColor, figureOpacity, story, loading, error } = useStory();

  if (error) return <>{i18n.t('error')}</>;
  if (loading && !story) return <>{i18n.t('loading')}</>;

  return (
    <Flex
      direction="column"
      borderBottom={`0.06rem solid ${hexToRgba(borderColor, 0.6)}`}
      borderStyle="dashed"
      m="0 0 1rem"
      w="100%"
    >
      <Flex position="relative" alignItems="start" justifyContent="space-between" w="100%">
        <Flex direction="column" w={['100%', '100%', '100%', '100%', 'calc(100% - (220px + 1rem))']} mb="1rem">
          <Breadcrumb
            height="32px"
            fontSize="0.875rem"
            transition="opacity .3s ease"
            color="var(--colors-breadcrumb)"
            m="0 0 1rem"
            opacity="0.6"
            _hover={{
              opacity: 1,
            }}
            sx={{
              '& ol': {
                minWidth: 0,
                height: '32px',
                display: 'flex',
                alignItems: 'center',
              },
              '& li,span': {
                '&[role=presentation]': {
                  fontSize: '0.875rem',
                  userSelect: 'none',
                },

                '&:last-child': {
                  textDecoration: 'none',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  cursor: 'default',
                },
              },
            }}
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                as={RouterLink}
                to={`/${i18n.language}/`}
                transition="color .1s ease-in-out"
                _focus={{
                  boxShadow: 'none',
                }}
                _active={{
                  color: 'var(--colors-breadcrumbActive)',
                }}
                _hover={{
                  color: 'var(--colors-breadcrumbHover)',
                }}
              >
                <FiHome size={14} />
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink
                transition="color .1s ease-in-out"
                _focus={{
                  boxShadow: 'none',
                }}
                _active={{
                  color: 'var(--colors-breadcrumbActive)',
                }}
                _hover={{
                  color: 'var(--colors-breadcrumbHover)',
                }}
              >
                {story.title}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Card as="article" lineHeight="1.875rem" p="0.75rem">
            <Helmet>
              {story?.title && <title>{getTitle(story.title + ' | news.24.md')}</title>}
              {story?.description && <meta name="description" content={story?.description} />}
              {story?.keywords && <meta name="keywords" content={story?.keywords} />}
              <meta property="og:type" content="article" />
              <meta property="og:site_name" content="news.24.md" />
              <meta property="og:url" content={process.env.REACT_APP_CLIENT_HOST + pathname} />
              {story?.title && <meta property="og:title" content={story?.title} />}
              {story?.description && <meta property="og:description" content={story?.description} />}
              {story?.image && <meta property="og:image:width" content="600" />}
              {story?.image && <meta property="og:image:height" content="315" />}
              {story?.image && <meta property="og:image" content={story?.image} />}
              {story?.title && <meta name="twitter:title" content={story?.title} />}
              {story?.description && <meta name="twitter:description" content={story?.description} />}
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:site" content="@news24moldova" />
              {story?.image && <meta name="twitter:image" content={story?.image} />}
              <link rel="canonical" href={process.env.REACT_APP_CLIENT_HOST + story.url} />
            </Helmet>
            <Flex display="inline" verticalAlign="top">
              <Flex
                float="right"
                display="inline-flex"
                alignItems="center"
                word-wrap="no-wrap"
                fontSize="0.9rem"
                lineHeight="1.2"
                m={[
                  '.25rem 0 0 0.15rem',
                  '.40rem 0 0 0.20rem',
                  '.60rem 0 0 0.30rem',
                  '.90rem 0 0 0.45rem',
                  '1.2rem 0 0 0.60rem',
                ]}
              >
                <Flex alignItems="center" lineHeight="1" textTransform="capitalize">
                  <FiClock style={{ marginRight: '0.25rem' }} />
                  <Date createdAt={story.createdAt} />
                </Flex>
                <Flex alignItems="center" lineHeight="1" marginLeft="1rem">
                  <FiEye style={{ marginRight: '0.25rem' }} />
                  {story.views}
                </Flex>
              </Flex>
              <Heading
                as="h1"
                fontSize={['1.2rem', '1.5rem', '1.7rem', '2rem', '2.5rem']}
                color="var(--colors-cardTitleColor)"
                m="0 0 1rem"
              >
                {story.title}
              </Heading>
            </Flex>
            <Flex justifyContent="center" id="M802285ScriptRootC1307866" />
            {story.intro && (
              <Text fontSize="1.2rem" fontWeight={700} m="1rem 0">
                {story.intro}
              </Text>
            )}
            {story.lead && (
              <Text fontSize="1rem" m="1rem 0">
                {story.lead}
              </Text>
            )}
            {!story.hasContentImage && story.image && (
              <Image
                src={story.image}
                alt={story.title}
                sx={{
                  width: 'calc(100% + 1.5rem)',
                  maxWidth: 'calc(100% + 1.5rem)',
                  transform: 'translateX(-50%)',
                  borderRadius: '0.375rem',
                  opacity: figureOpacity,
                  ml: '50%',
                }}
              />
            )}
            {story.content && (
              <Text
                as="div"
                fontSize="1rem"
                m="1rem 0"
                sx={{
                  '& ul': {
                    listStylePosition: 'initial',
                    pl: '1rem',
                  },
                  '& div,p,ul': {
                    m: '1rem 0',
                  },
                  '& blockquote': {
                    p: '0 3rem',
                    m: '1rem 0',
                  },
                  '& strong,b': {
                    fontSize: '1.1rem',
                  },
                  '& img': {
                    width: 'calc(100% + 1.5rem)',
                    maxWidth: 'calc(100% + 1.5rem)',
                    transform: 'translateX(-50%)',
                    borderRadius: '0.375rem',
                    opacity: figureOpacity,
                    m: '1rem 50%',
                  },
                  '& iframe': {
                    // width: 'calc(100% + 1.5rem)',
                    maxWidth: 'calc(100% + 1.5rem)',
                    // minH: '56%',
                    transform: 'translateX(-50%)',
                    borderRadius: '0.375rem',
                    m: '1rem 50%',
                    // https://stackoverflow.com/questions/50031283/how-to-maintain-aspect-ratio-between-169-and-43-using-css
                    // https://stackoverflow.com/questions/3846132/jquery-get-height-of-iframe-content-when-loaded
                    // https://www.tutorialrepublic.com/faq/automatically-adjust-iframe-height-according-to-its-contents-using-javascript.php
                    // https://www.dyn-web.com/tutorials/iframes/height/
                    // https://dev.to/ben/how-would-you-go-about-detecting-the-height-of-content-within-an-iframe-14d8
                    // height: '100%',
                    // aspectRatio: 'auto 16 / 9',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: story.content,
                }}
              />
            )}
            <Flex alignItems="center" justifyContent="space-between" fontSize="0.9rem" m="1rem 0 0">
              <Flex alignItems="center" lineHeight="1">
                <Link
                  fontWeight={700}
                  textDecoration="none"
                  rel="nofollow noindex noreferrer noopener"
                  target="_blank"
                  _focus={{
                    textDecoration: 'none',
                  }}
                  _active={{
                    textDecoration: 'none',
                  }}
                  _hover={{
                    textDecoration: 'none',
                  }}
                  href={`//${story.source}`}
                >
                  {story.source}
                </Link>
              </Flex>
              <Flex>
                <Share data={story.sharer} />
              </Flex>
            </Flex>
            <Flex w="100%" m="1rem 0 0" direction="column" justifyContent="center" id="M802285ScriptRootC1307868" />
          </Card>
          <Flex m="0.85rem 0 0" direction="column">
            <Widget title={i18n.t('main.widgets.followUs.title')} content={<FollowUs />} />
          </Flex>
        </Flex>
        <Sticky as="aside" w="220px" top="0">
          <Widget
            position={Position.Right}
            title={i18n.t('main.widgets.share.title')}
            content={<ListShare position={Position.Right} data={story.sharer} />}
          />
        </Sticky>
      </Flex>

      <Flex m="0.85rem 0 1.85rem 0" direction="column">
        <Widget
          title={i18n.t('main.widgets.related.title')}
          content={<Related hash={story.hash} title={story.title} />}
        />
      </Flex>
    </Flex>
  );
}

export default Story;
